/**
 * Settings › Organization — `organization-build-book.md` §6.8,
 * `org-08-organization-tree.png`.
 *
 * The company's shape: regions, districts and the branches where work
 * actually happens, and any level may be skipped — a one-branch company is a
 * company and a branch, and that has to look ordinary rather than like
 * something half set up.
 *
 * The tree is the navigation and the card is the record, with `?unit=` in the
 * URL so a branch can be linked to. There is no separate "branch page": a
 * branch is not a record anybody opens from a list, it is a place in a shape,
 * and the shape is the only useful way to find it.
 */
import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { MeResponse, OrgUnitDetail, OrgUnitKind, OrgUnitRow } from '@rowted/shared'
import { can } from '@rowted/shared'
import { request } from '../lib/api.js'
import { Button, EmptyState } from '../components/primitives.js'
import { OrgTree } from '../components/org/org-tree.js'
import { OrgDetail } from '../components/org/org-detail.js'
import { AddUnitDialog, MoveUnitDialog } from '../components/org/dialogs.js'
import { ConfirmDialog } from '../components/overlays.js'
import { IconPlus } from '../frame/icons.js'
import { useSettingsBar } from '../settings/settings-bar.js'

export function Organization({ me }: { me: MeResponse }) {
  const [params, setParams] = useSearchParams()
  const [units, setUnits] = useState<OrgUnitRow[]>([])
  const [detail, setDetail] = useState<OrgUnitDetail | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [adding, setAdding] = useState<{ parent: OrgUnitRow; kind?: OrgUnitKind } | null>(null)
  const [moving, setMoving] = useState<{ unit: OrgUnitRow; ontoId?: string } | null>(null)
  const [closing, setClosing] = useState<OrgUnitDetail | null>(null)
  const [openVisits, setOpenVisits] = useState<number | null>(null)

  const canEdit = can(me.permissions, 'company_settings', 'edit')
  const selectedId = params.get('unit')
  const tab = params.get('tab') ?? 'details'

  const select = useCallback((id: string, nextTab = 'details') => {
    setParams((was) => {
      const next = new URLSearchParams(was)
      next.set('unit', id)
      next.set('tab', nextTab)
      return next
    }, { replace: true })
  }, [setParams])

  const loadTree = useCallback(async () => {
    try {
      const { items } = await request<{ items: OrgUnitRow[] }>('/org-units')
      setUnits(items)
      setError(null)
      return items
    } catch (problem) {
      setError((problem as { message?: string }).message ?? 'The organization did not load.')
      return []
    }
  }, [])

  useEffect(() => { void loadTree() }, [loadTree])

  /* Nothing chosen yet: the company, which is the one row everybody has. */
  useEffect(() => {
    if (selectedId || units.length === 0) return
    const root = units.find((one) => one.kind === 'company' && one.reachable)
      ?? units.find((one) => one.reachable)
    if (root) select(root.id)
  }, [selectedId, units, select])

  useEffect(() => {
    if (!selectedId) { setDetail(null); return }
    let live = true
    void request<OrgUnitDetail>(`/org-units/${selectedId}`)
      .then((one) => { if (live) { setDetail(one); setError(null) } })
      .catch((problem: { message?: string }) => {
        if (live) setError(problem.message ?? 'That place did not load.')
      })
    return () => { live = false }
  }, [selectedId])

  const selectedRow = units.find((one) => one.id === selectedId) ?? null
  const company = units.find((one) => one.kind === 'company') ?? null

  const barActions = useSettingsBar({
    title: 'Organization',
    sub: 'regions › districts › branches, with their yards and zones · any level can be skipped',
    actions: canEdit && company
      ? (
        /*
         * One button, not one per kind.
         *
         * There were two — "Add region" and "Add branch" — and no third for a
         * district, which made a district look like something the product
         * does not really have. It always did: the dialog asks which kind and
         * where, and both buttons only ever pre-answered it. So the bar asks
         * for a place and the dialog asks what kind of place, which is also
         * one button fewer to fit on a phone.
         */
        <Button
          variant="primary"
          className="h-8 text-[12.5px]"
          onClick={() => setAdding({ parent: addUnder(selectedRow, units, company) })}
        >
          <IconPlus size={15} />Add place
        </Button>
      )
      : undefined,
  })

  /** Both panels want the tree and the card back after a write. */
  const afterWrite = useCallback(async (one: OrgUnitDetail) => {
    setDetail(one)
    await loadTree()
  }, [loadTree])

  async function close(moveOpenVisitsToParent: boolean) {
    if (!closing) return
    try {
      const one = await request<OrgUnitDetail>(`/org-units/${closing.id}/close`,
        { method: 'POST', body: { moveOpenVisitsToParent } })
      setClosing(null)
      setOpenVisits(null)
      await afterWrite(one)
    } catch (problem) {
      const body = problem as { message?: string; details?: { openVisits?: number } }
      if (body.details?.openVisits) {
        // Not an error to show and give up on: it is the question the dialog
        // exists to ask, now that it knows the number.
        setOpenVisits(body.details.openVisits)
        return
      }
      setError(body.message ?? 'That did not close.')
      setClosing(null)
    }
  }

  async function reopen() {
    if (!detail) return
    try {
      await afterWrite(await request<OrgUnitDetail>(`/org-units/${detail.id}/reopen`,
        { method: 'POST', body: {} }))
    } catch (problem) {
      setError((problem as { message?: string }).message ?? 'That did not reopen.')
    }
  }

  return (
    <div className="flex items-start gap-5 p-5 max-lg:flex-col">
      {barActions}

      <OrgTree
        units={units}
        selectedId={selectedId}
        onSelect={(id) => select(id, tab)}
        onDropOn={canEdit
          ? (movedId, ontoId) => {
            const unit = units.find((one) => one.id === movedId)
            if (unit) setMoving({ unit, ontoId })
          }
          : undefined}
      />

      {detail
        ? (
          <OrgDetail
            me={me}
            unit={detail}
            units={units}
            canEdit={canEdit}
            tab={tab}
            onTab={(next) => select(detail.id, next)}
            onSaved={(one) => void afterWrite(one)}
            onSelect={(id) => select(id)}
            onMove={() => selectedRow && setMoving({ unit: selectedRow })}
            onAddBelow={() => selectedRow && setAdding({ parent: selectedRow })}
            onClose={() => { setOpenVisits(null); setClosing(detail) }}
            onReopen={() => void reopen()}
          />
        )
        : (
          <div className="min-w-0 flex-1 rounded-record border border-line bg-surface">
            <EmptyState
              title={error ?? 'Choose a place'}
              body={error
                ? 'Try again, or ask somebody who can reach it.'
                : 'Pick a region, district or branch on the left to see and change it.'}
            />
          </div>
        )}

      {adding && (
        <AddUnitDialog
          parent={adding.parent}
          kind={adding.kind}
          units={units}
          onClose={() => setAdding(null)}
          onAdded={(one) => {
            setAdding(null)
            void loadTree().then(() => select(one.id))
          }}
        />
      )}

      {moving && (
        <MoveUnitDialog
          unit={moving.unit}
          units={units}
          ontoId={moving.ontoId}
          onClose={() => setMoving(null)}
          onMoved={(one) => { setMoving(null); void afterWrite(one) }}
        />
      )}

      {closing && (
        <ConfirmDialog
          title={`Close ${closing.name}?`}
          confirmLabel={openVisits ? 'Move them and close' : `Close ${closing.kind}`}
          busyLabel="Closing…"
          cancelLabel="Keep it open"
          onCancel={() => { setClosing(null); setOpenVisits(null) }}
          onConfirm={() => void close(openVisits !== null)}
          body={openVisits
            ? (
              <>
                {openVisits} open visit{openVisits === 1 ? '' : 's'} still belong
                {openVisits === 1 ? 's' : ''} to {closing.name}. Closing it moves them to{' '}
                {closing.parentName ?? 'the company'}&apos;s unassigned queue; nothing is lost and
                nothing is deleted.
              </>
            )
            : (
              <>
                {closing.name} stops being a place you can schedule, quote or hold stock at. Its
                people keep their grants, its history stays on every report, and you can reopen
                it at any time.
              </>
            )}
        />
      )}
    </div>
  )
}

/**
 * Where "Add place" starts from.
 *
 * The row you are looking at, when it can hold something; its parent when it
 * is a branch, because what you usually want next to a branch is another
 * branch; the company when nothing is selected. The dialog can still be
 * pointed anywhere — this is only what it opens on.
 */
function addUnder(
  selected: OrgUnitRow | null, units: OrgUnitRow[], company: OrgUnitRow,
): OrgUnitRow {
  if (!selected) return company
  if (selected.kind !== 'branch') return selected
  return units.find((one) => one.id === selected.parentId) ?? company
}
